import {
  BoundForm,
  boundSelectCardGroupField,
  boundSelectField,
  boundTextAreaField,
  boundTextField,
  Css,
  IconKey,
  SelectCardGridGroupItemOption,
  useComputed,
  useTestIds,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { ReactNode } from "react";
import { ReadyPlanOptionGroupPersonalizationType } from "src/generated/graphqlTypes";
import { PlanEditorHeader } from "src/routes/components/PlanConfiguration/PlanEditorHeader";
import { PackageDisplayOptionField } from "src/routes/libraries/design-catalog/components/PackageDisplayOptionField";
import { EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment } from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import {
  SlotsSection,
  TargetGroupsSection,
} from "src/routes/libraries/design-catalog/personalization-option/components/SlotsSection";
import {
  canAssignTargetCombinations,
  FormValue,
} from "src/routes/libraries/design-catalog/personalization-option/utils";
import { zeroTo } from "src/utils";

type DetailsStepProps = {
  formState: ObjectState<FormValue>;
  title: string;
  personalizationTypes: { code: ReadyPlanOptionGroupPersonalizationType; name: string }[];
  /** True when editing an existing option (type cannot be changed). */
  isExisting: boolean;
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[];
};

/**
 * The first step: option details (name/type/levels), the target groups it applies to ("Apply to"),
 * and the slots it controls.
 */
export function DetailsStep(props: DetailsStepProps) {
  const { formState, title, personalizationTypes, isExisting, targetGroups } = props;
  const tid = useTestIds({});
  const isPackage = useComputed(
    () => formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package,
    [formState],
  );
  const showApplyTo = useComputed(() => canAssignTargetCombinations(formState.type.value), [formState]);

  const personalizationTypeConfig: Record<
    ReadyPlanOptionGroupPersonalizationType,
    { icon: IconKey; name: string; description: string }
  > = {
    [ReadyPlanOptionGroupPersonalizationType.SingleProduct]: {
      name: "Single",
      icon: "single",
      description: "Slots share the same options, but each slot can have its own selection.",
    },
    [ReadyPlanOptionGroupPersonalizationType.LinkedProduct]: {
      icon: "linked",
      name: "Linked",
      description: "Slots share the same options and always have the same selection.",
    },
    [ReadyPlanOptionGroupPersonalizationType.Package]: {
      icon: "package",
      name: "Package",
      description: "Selections come in preconfigured bundles.",
    },
  };

  return (
    <Observer>
      {() => (
        <div css={Css.bgWhite.fg1.oya.df.fdc.$} {...tid}>
          <PlanEditorHeader title={title} />
          <div css={Css.df.fdc.gap("48px").wPx(678).asc.pt5.pb5.$}>
            <Section title="Details">
              <BoundForm
                formState={formState}
                rows={[
                  {
                    type: boundSelectCardGroupField({
                      label: "Option Type *",
                      options: personalizationTypes.map((type) => {
                        const { icon, description, name } = personalizationTypeConfig[type.code];
                        return {
                          icon,
                          description,
                          label: name,
                          value: type.code,
                          disabled: isExisting,
                          tooltip: isExisting ? "Type cannot be modified for existing options" : undefined,
                        } satisfies SelectCardGridGroupItemOption<ReadyPlanOptionGroupPersonalizationType>;
                      }),
                    }),
                  },
                  { name: boundTextField({}) },
                  { marketingDescription: boundTextAreaField({}) },
                  {
                    levels: boundSelectField({
                      options: [{ name: "", value: null }, ...zeroTo(5, (i) => ({ name: `${i + 1}`, value: i + 1 }))],
                      getOptionLabel: (v) => v.name,
                      getOptionValue: (v) => v.value,
                    }),
                  },
                  ...(isPackage
                    ? [
                        {
                          reactNodeDisplayPackageOptionsAsSingleProduct: (fs: ObjectState<FormValue>) => (
                            <PackageDisplayOptionField field={fs.displayPackageOptionsAsSingleProduct} />
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </Section>

            {showApplyTo && (
              <Section title="Apply To">
                <TargetGroupsSection formState={formState} tid={tid} targetGroups={targetGroups} />
              </Section>
            )}

            <Section title="Slots" subtitle="Slot order defines their display in Checkout.">
              <SlotsSection formState={formState} tid={tid} targetGroups={targetGroups} />
            </Section>
          </div>
        </div>
      )}
    </Observer>
  );
}

function Section(props: { title: string; subtitle?: string; children: ReactNode }) {
  const { title, subtitle, children } = props;
  return (
    <section css={Css.df.fdc.gap3.$}>
      <div css={Css.df.fdc.gap1.$}>
        <h2 css={Css.xl.gray900.$}>{title}</h2>
        {subtitle && <p css={Css.sm.gray600.$}>{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}
